import { useIsFocused, useRoute } from '@react-navigation/native';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppState, AppStateStatus } from 'react-native';

import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { ContactType } from '@components/Invite/Contact';
import { Box } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { FilterToolbarSkeleton } from '@components/shared/skeletons';
import { MyTaskList } from '@components/TaskList/MyTaskList.web';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { TasksFilterBar } from '@components/Web/TasksFilterBar';
import {
  Project,
  SearchableResult,
  TaskMemberRole,
  TaskPriority,
  TaskSortOption,
  User,
  useSearchLazyQuery,
} from '@graphql/generated';
import { useRefreshTeamSubscriptions } from '@hooks/useProjectTeamSubscription';
import { useRefetchOnFocus } from '@hooks/useRefetchOnFocus';
import { TaskSearchParam, useTaskSections } from '@hooks/useTaskSections';
import { TasksTabStackScreenProps } from '@navigation/tasks/tasks-tab-stack';
import { Search } from '@root/types';
import {
  MY_TASKS_SAFETY_POLL_INTERVAL_MS,
  TASK_LIST_REFETCH_MIN_AGE_MS,
} from '@src/constants/constants';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';
import { settleAllOrThrow } from '@utils/settleAllOrThrow';
import { filterByDateRange } from '@utils/tasks';
import { isTaskSearchResult } from '@utils/typeGuards';

import type { ProjectUpdatedRefreshMode } from '@hooks/useRefreshOnProjectUpdated';

const DEBOUNCE = 500;
export type QuickDueLabel = 'Today' | 'Tomorrow' | 'Next 3 days';

export type QuickDateRange = {
  start: Date;
  end: Date;
};

/**
 * Map quick due-date label to a start/end date range.
 * NOTE:
 * - "Today"    => today 00:00:00 ~ today 23:59:59.999
 * - "Tomorrow" => tomorrow 00:00:00 ~ tomorrow 23:59:59.999
 * - "Next 3 days" => from tomorrow to 3 days later (3 full days, not including today)
 */
export const getDateRangeForLabel = (
  label: QuickDueLabel
): QuickDateRange | null => {
  const now = dayjs();

  switch (label) {
    case 'Today': {
      const start = now.startOf('day');
      const end = now.endOf('day');
      return { start: start.toDate(), end: end.toDate() };
    }

    case 'Tomorrow': {
      const start = now.add(1, 'day').startOf('day');
      const end = now.add(1, 'day').endOf('day');
      return { start: start.toDate(), end: end.toDate() };
    }

    case 'Next 3 days': {
      // from tomorrow to 3 days later (exclude today)
      const start = now.add(1, 'day').startOf('day');
      const end = now.add(3, 'day').endOf('day');
      return { start: start.toDate(), end: end.toDate() };
    }

    default:
      return null;
  }
};

export const Tasks: React.FC = () => {
  const [search] = useState('');
  const [_refreshing, _setRefreshing] = useState(false);
  const [isMaximize] = useState(false);

  const [selectedDueDateStrList, setSelectedDueDateStrList] = useState<
    string[]
  >([]);

  const [selectedSortList, setSelectedSortList] = useState<TaskSortOption[]>(
    []
  );

  const sortBy: TaskSortOption =
    selectedSortList.length > 0 ? selectedSortList[0] : TaskSortOption.Rank;

  const [selectedProjects, setSelectedProjects] = useState<Project[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<ContactType[]>([]);
  const [selectedDueDateRange, setSelectedDueDateRange] = useState<DateRange>({
    start: undefined,
    end: undefined,
  });
  const [selectedPriorities, setSelectedPriorities] = useState<TaskPriority[]>(
    []
  );

  const dueDateBeginParam = () => {
    if (selectedDueDateRange.start) {
      return dayjs(selectedDueDateRange.start)
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm:ssZ');
    } else if (selectedDueDateStrList.length > 0) {
      const range = getDateRangeForLabel(
        selectedDueDateStrList[0] as QuickDueLabel
      );
      if (range)
        return dayjs(range.start).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
    }
    return undefined;
  };
  const dueDateEndParam = () => {
    if (selectedDueDateRange.end) {
      return dayjs(selectedDueDateRange.end)
        .endOf('day')
        .format('YYYY-MM-DDTHH:mm:ssZ');
    } else if (selectedDueDateStrList.length > 0) {
      const range = getDateRangeForLabel(
        selectedDueDateStrList[0] as QuickDueLabel
      );
      if (range)
        return dayjs(range.end).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ');
    }

    return undefined;
  };

  // TA-11139 (web): same as native's TasksListView — refresh on each
  // projectUpdated payload, with a slow safety-net poll, only while this tab
  // is focused and the browser tab is in the foreground. An event that lands
  // meanwhile is refreshed once on the way back.
  const isFocused = useIsFocused();
  const [isAppActive, setIsAppActive] = useState(true);
  useEffect(() => {
    const handleAppStateChange = (nextState: AppStateStatus) =>
      setIsAppActive(nextState === 'active');
    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange
    );
    return () => subscription.remove();
  }, []);
  const isMyTasksLive = isFocused && isAppActive;
  let myTasksRefreshMode: ProjectUpdatedRefreshMode = 'background';
  if (isAppActive) myTasksRefreshMode = isFocused ? 'live' : 'paused';
  const myTasksPollIntervalMs = isMyTasksLive
    ? MY_TASKS_SAFETY_POLL_INTERVAL_MS
    : undefined;
  // TA-10958: team billing left the listTasks rows, so poll it alongside
  // them; otherwise a remote cancel or reactivation stays stale in the
  // paywall checks until the list remounts. TA-11139: on the safety-net
  // interval now; billing changes publish no projectUpdated.
  useRefreshTeamSubscriptions({ pollInterval: myTasksPollIntervalMs });

  const param: TaskSearchParam = {
    skip: !!search,
    // fetchPolicy intentionally omitted — useTaskSections defaults to
    // cache-and-network so switching project filters revalidates against the
    // server instead of replaying a stale per-filter cache entry (TA-10472).
    pollInterval: myTasksPollIntervalMs,
    refreshOnProjectUpdated: myTasksRefreshMode,
    projectIds:
      selectedProjects.length > 0
        ? selectedProjects.map((p) => p.id)
        : undefined,
    priority:
      selectedPriorities.length > 0 ? [...selectedPriorities] : undefined,
    dueDateBegin: dueDateBeginParam(),
    dueDateEnd: dueDateEndParam(),
    memberIds:
      selectedAssignees.length > 0
        ? selectedAssignees.map((t) => t.id)
        : undefined,
  };

  const { groupedCurrentTasks, groupedOverDueTasks, groupedCompletedTasks } =
    useTaskSections(param, sortBy, true);

  // True only on the very first load (every section still loading with no
  // data yet) — mirrors isInitialTaskListLoading in MyTaskList.web.tsx, used
  // here to skeleton the filter/sort/add-to-calendar toolbar so a user can't
  // open one of those controls before the task list it acts on has loaded.
  // hasLoadedTasksOnceRef latches once all sections have finished loading a
  // single time, so a later filter/sort change (which briefly resets
  // loading+empty for a new, uncached variable combo) doesn't re-trigger the
  // skeleton and remount TasksFilterBar, closing its open dropdown (TA-10949 #6).
  const hasLoadedTasksOnceRef = useRef(false);
  useEffect(() => {
    if (
      !groupedCurrentTasks.loading &&
      !groupedOverDueTasks.loading &&
      !groupedCompletedTasks.loading
    ) {
      hasLoadedTasksOnceRef.current = true;
    }
  }, [
    groupedCurrentTasks.loading,
    groupedOverDueTasks.loading,
    groupedCompletedTasks.loading,
  ]);
  const isInitialTasksLoad =
    !hasLoadedTasksOnceRef.current &&
    groupedCurrentTasks.loading &&
    groupedOverDueTasks.loading &&
    groupedCompletedTasks.loading &&
    groupedCurrentTasks.sectionData.data.length === 0 &&
    groupedOverDueTasks.sectionData.data.length === 0 &&
    groupedCompletedTasks.sectionData.data.length === 0;

  const refetch = useCallback(async () => {
    // settleAllOrThrow (not Promise.allSettled) so a failed/partial section
    // refetch rejects and useRefetchOnFocus does NOT advance its staleness
    // clock — next focus retries promptly (TA-10046).
    await settleAllOrThrow([
      groupedCurrentTasks.refetch(),
      groupedOverDueTasks.refetch(),
      groupedCompletedTasks.refetch(),
    ]);
  }, [
    groupedCurrentTasks.refetch,
    groupedOverDueTasks.refetch,
    groupedCompletedTasks.refetch,
  ]);

  useRefetchOnFocus(refetch, TASK_LIST_REFETCH_MIN_AGE_MS);
  // #region Global Search
  const [searchLoading, setSearchLoading] = useState(false);
  const [globalSearch, { data, error }] = useSearchLazyQuery();

  const { setIsTaskWebPanelOpen, setIdTaskDetailOpen, setMainPanelWidth } =
    useWebDrawer();

  const route = useRoute<TasksTabStackScreenProps<'tasks'>['route']>();
  const routeTaskId = route.params?.taskId;

  useEffect(() => {
    if (routeTaskId) {
      setIdTaskDetailOpen(routeTaskId);
      setIsTaskWebPanelOpen(true);
    }
  }, [routeTaskId]);

  const taskResults =
    (data &&
      (data.search as Search<SearchableResult>[])
        .filter(isTaskSearchResult)
        .map((r) => r.record)) ||
    [];

  const globalSearchCall = () =>
    globalSearch({
      variables: {
        term: search,
        size: 20,
        includeMessages: false,
        includeDocuments: false,
        includeProjects: false,
        includeTasks: true,
      },
    });

  const debouncedGlobalSearch = useCallback(
    debounce(globalSearchCall, DEBOUNCE),
    [search]
  );
  useEffect(() => {
    setSearchLoading(false);
  }, [data, error]);

  useEffect(() => {
    if (search) {
      setSearchLoading(true);
      debouncedGlobalSearch();
    }
    return () => debouncedGlobalSearch.cancel();
  }, [search]);
  // #endregion

  // #region Filter
  const [dateRange] = useState<DateRange>({});
  const filteredTasks = search
    ? filterByDateRange(taskResults, ({ dueDate }) => dueDate, dateRange)
    : [
        ...groupedCurrentTasks.sectionData.data,
        ...groupedOverDueTasks.sectionData.data,
        ...groupedCompletedTasks.sectionData.data,
      ];
  // #endregion

  // Assignee filter options (TA-10949, product decision 2026-10-07): built
  // from the tasks already loaded for the screen, same source + no new query
  // as mobile's TasksListView (~L725, knownAssigneesRef). The task data here
  // is itself scoped by the current selectedAssignees (via param.memberIds
  // above), so a naive useMemo keyed on that data would narrow/reorder its
  // own options on every pick (QA round 3). Accumulating ids into a ref
  // across renders instead means a person who dropped out of the
  // now-filtered task set stays listed as long as they were ever seen in a
  // loaded task this session — options only grow, never shrink from a
  // filter pick. isInitialTasksLoad above already skeletons TasksFilterBar
  // out of the tree until the first load resolves, so this never renders
  // against a still-empty "No results found" state on a cold load either.
  const knownAssigneesRef = useRef(new Map<string, User>());
  const assigneeList = useMemo(() => {
    [
      ...groupedCurrentTasks.sectionData.data,
      ...groupedOverDueTasks.sectionData.data,
      ...groupedCompletedTasks.sectionData.data,
    ]
      .flatMap((t) =>
        t.members.filter((m) => m.role === TaskMemberRole.Assignee)
      )
      .forEach((tm) => {
        if (!knownAssigneesRef.current.has(tm.user.id)) {
          knownAssigneesRef.current.set(tm.user.id, tm.user);
        }
      });

    return convertUsersToContacts([...knownAssigneesRef.current.values()]);
  }, [
    groupedCurrentTasks.sectionData.data,
    groupedOverDueTasks.sectionData.data,
    groupedCompletedTasks.sectionData.data,
  ]);

  return (
    <Box
      flex={1}
      onLayout={(e) => setMainPanelWidth(e.nativeEvent.layout.width)}>
      <Box flex={1} flexDirection='row' justifyContent='space-between'>
        {!isMaximize && (
          <Box flex={1} mt='xs'>
            <Box flexDirection='row' alignItems='center'>
              {isInitialTasksLoad ? (
                <Box ml='m' mr='s' mt='xs' mb='xxs'>
                  <FilterToolbarSkeleton />
                </Box>
              ) : (
                <TasksFilterBar
                  initialAssignees={assigneeList}
                  selectedProjects={selectedProjects}
                  setSelectedProjects={setSelectedProjects}
                  selectedAssignees={selectedAssignees}
                  setSelectedAssignees={setSelectedAssignees}
                  selectedPriorities={selectedPriorities}
                  setSelectedPriorities={setSelectedPriorities}
                  selectedDueDateRange={selectedDueDateRange}
                  setSelectedDueDateRange={setSelectedDueDateRange}
                  selectedDueDateStrList={selectedDueDateStrList}
                  setSelectedDueDateStrList={setSelectedDueDateStrList}
                  setSelectedSortList={setSelectedSortList}
                  selectedSortList={selectedSortList}
                />
              )}
            </Box>
            <Box mt='xs'></Box>
            {searchLoading && (
              <Box mt='xxs'>
                <ActivityIndicatorLoading />
              </Box>
            )}
            {!searchLoading && (
              <MyTaskList
                tasks={
                  search
                    ? filteredTasks
                    : [
                        groupedCurrentTasks,
                        groupedOverDueTasks,
                        groupedCompletedTasks,
                      ]
                }
                onPress={(item, _) => {
                  setIdTaskDetailOpen(item.id);
                  setIsTaskWebPanelOpen(true);
                }}
                draggable={sortBy === TaskSortOption.Rank}
                isArrowVisible={true}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
